import type { ColorTabs } from '@src/components/common/ColorPicker';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';
import type { RoleLineSettings } from '@src/utility/speakerRole';

export enum CompositionType {
  INTRO_CARD = 'introCard',
  END_CARD = 'endCard',
  NAME_CARD = 'nameCard',
  QUOTE_CARD = 'quoteCard',
  OTHER = 'other',
  OVERLAY = 'overlay',
}

export interface CompositionTemplate {
  id: string;
  label: string;
  internalName: string;
  aspectRatio: string;
  source: Source;
  compositionType: CompositionType;
  isFullscreen: boolean;
  controls: ControlConfiguration[]; // parsed array of control definitions
  thumbnail?: { id?: string; url?: string };
  placement?: CompositionPlacement | null;
  overlapBefore?: number | null;
  overlapAfter?: number | null;
  sixteenByNine?: AspectConfiguration | null;
  nineBySixteen?: AspectConfiguration | null;
  oneByOne?: AspectConfiguration | null;
  isQuoteCard?: boolean;
  isGlobalPreset?: boolean;
  parentComposition?: { id: string };
  isDeleted?: boolean;
}

export interface Category {
  id: string;
  label: string;
  order: number;
  subcategories: Subcategory[];
}

export interface Subcategory {
  id: string;
  label: string;
  order: number;
  compositions: CompositionTemplate[];
}

export interface AspectConfiguration {
  positionX?: string | null;
  positionY?: string | null;
  // Legacy uniform scale, applied to BOTH axes. Records written before per-axis
  // support carry only this field; `scaleX`/`scaleY` fall back to it, and since
  // its meaning has always been "both axes" that fallback is exact — no backfill
  // needed. Resolve via CompositionNormalizer.resolveAxisScales, never directly.
  scale?: string | number | null;
  // Per-axis multipliers (e.g. 0.8, 1.2) or strings ("0.8" | "120%"). Two axes
  // are needed because the canvas allows non-uniform resizes of a composition,
  // which a single `scale` silently truncated to its width component.
  scaleX?: string | number | null;
  scaleY?: string | number | null;
}

export type AspectConfigKey = 'sixteenByNine' | 'nineBySixteen' | 'oneByOne';

export interface Source extends Record<string, unknown> {
  id?: string | null;
  name?: string | null;
  width?: string | number | null;
  height?: string | number | null;
  x?: string | number | null;
  y?: string | number | null;
  elements?: AnyElement[];
}

export enum CompositionPlacement {
  /**
   * CARD placement: before the clip. Inserts at the beginning of the timeline and
   * ripples existing content. An intro card *defines* the clip's start bound — it
   * does not follow it. For a graphic that follows that bound, use `CLIP_START`.
   */
  START = 'start',
  /**
   * CARD placement: after the clip. Inserts at the end of the timeline, after all
   * existing content. An end card *defines* the clip's end bound — it does not
   * follow it. For a graphic that follows that bound, use `CLIP_END`.
   */
  END = 'end',
  CURRENT_TIME = 'current_time',
  /** Inserts at end of current clip, pushing subsequent elements (except early music) */
  SECTION = 'section',
  /** Spans the card-bounded clip and keeps following those bounds (PLAT-10907 E7). */
  CLIP = 'clip',
  /**
   * GRAPHIC placement (PLAT-10921): pinned to the start of the clip's CONTENT —
   * where the intro card stops — and keeps following it. Distinct from `START`,
   * which puts a card *outside* the clip at the timeline start.
   */
  CLIP_START = 'clip-start',
  /**
   * GRAPHIC placement (PLAT-10921): pinned to the end of the clip's CONTENT —
   * where the end card starts — and keeps following it. Distinct from `END`,
   * which puts a card *outside* the clip after all content.
   */
  CLIP_END = 'clip-end',
  /** Bound to the anchored words of the passage the graphic sits over (PLAT-10907 E7). */
  SELECTION = 'selection',
}

export enum DefaultBrandKitValue {
  PRIMARY_COLOR = 'primaryColor',
  SECONDARY_COLOR = 'secondaryColor',
  DEFAULT_FONT = 'defaultFont',
  WEBSITE = 'website',
}

export enum QuoteControlType {
  TEXT = 'text',
  NAME = 'name',
  ORG = 'org',
  BACKGROUND = 'background',
  HEADSHOT = 'headshot',
}

export interface ControlConfigurationBase {
  label: string;
  /**
   * A label the author chose for this control (PLAT-11993). When set it wins
   * over the `{Element} {Attribute}` label normalization derives from the
   * element's name, so a setting ("Name Color" → "Accent") can be renamed on
   * its own. Absent means "derive it", and renaming the element then renames
   * the setting too.
   */
  customLabel?: string;
  type: string;
  elementPaths: number[][]; // numeric index paths into active element tree
  property: string;
  customValue?: string | number | unknown;
  default?: {
    value?: string | number;
    brandKit?: DefaultBrandKitValue;
    quote?: QuoteControlType;
  };
  unit?: string;
  // Optional stable identifier used for meta control mapping
  id?: string;
  enabled?: boolean;
  // Translation flags (PLAT-9963). Required: `id` must be set when either flag is true.
  allowTranslation?: boolean;
  allowQuickEdit?: boolean;
  // Author-authored preset translations keyed by ISO language code. Only consulted
  // when allowTranslation is true. Locked content — see PLAT-9870 preset lock contract.
  translations?: { [languageCode: string]: string };
}

/**
 * The speaker field a nametag control can be bound to (PLAT-12176). `name`
 * and `title` are the speaker's two lines, typed or derived in the
 * workspace's Speaker Format; `firstName`, `lastName`, `role` (Position) and
 * `organization` (Company) are the facts on their own elements. `roleLine`
 * is phase 2's name for the Title line (PLAT-12085) and fills as `title`.
 */
export type SpeakerBindingKey =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'role'
  | 'organization'
  | 'roleLine'
  | 'headshot';

export interface SpeakerControlConfiguration extends ControlConfigurationBase {
  type: 'speaker';
  controlBindings: Record<string, SpeakerBindingKey>; // metaControlId -> speaker object key
  /**
   * Set by the Three Lines toggle when it adds the Company line (PLAT-12179):
   * the control that was the Title line, so turning it off makes that one
   * the Title again and drops the other — whether the third line was a clone
   * or a spare text the design already had.
   */
  companyLine?: { titleControlId: string };
  /**
   * @deprecated Phase 2's per-template role-line settings (PLAT-12085). Still
   * found on some `dev` templates; never read since PLAT-12176 — the format
   * is the showcase's Speaker Format (Brand Kit).
   */
  roleLine?: Partial<RoleLineSettings>;
}
export interface TextControlConfiguration extends ControlConfigurationBase {
  type: 'text';
}
export interface ColorControlConfiguration extends ControlConfigurationBase {
  type: 'color';
}
/** Text written by a model from the transcript (PLAT-10907 E1). */
export interface AITextControlConfiguration extends ControlConfigurationBase {
  type: 'aiText';
  /** DatoCMS `ai_prompt` record id — the prompt this control generates with. */
  promptId: string;
  /** Human-readable mirror of the prompt's title. Display + debugging only. */
  promptTitle?: string;
  /** Default base-text mode when the control is first shown. */
  defaultBaseTextScope?: AITextBaseTextScope;
}
export interface NumberControlConfiguration extends ControlConfigurationBase {
  type: 'number';
}
export interface FontControlConfiguration extends ControlConfigurationBase {
  type: 'font';
}
export interface ImageControlConfiguration extends ControlConfigurationBase {
  type: 'image';
}
export interface LogoControlConfiguration extends ControlConfigurationBase {
  type: 'logo';
}
export interface VideoControlConfiguration extends ControlConfigurationBase {
  type: 'video';
  /**
   * PLAT-11732 — the card takes the chosen video's length, rippling the
   * timeline after it. Set on a blank media card's video control.
   */
  matchCardDuration?: boolean;
}
export interface HeadshotControlConfiguration extends ControlConfigurationBase {
  type: 'headshot';
  controlBindings?: Record<string, 'headshot'>; // links to SpeakerControl's speaker headshot
}

/** What the first, automatic generation reads (PLAT-10907 E12). */
export type AITextBaseTextScope = 'clip' | 'selection';

/**
 * In-flight / error state for one `aiText` control on one graphic
 * (PLAT-10915). Lives here rather than beside the hook that produces it so a
 * presentational consumer can render the shape without importing the hook —
 * and with it the service and repository behind it.
 */
export interface AITextControlStatus {
  isGenerating: boolean;
  error: string | null;
}

/** The status a surface reports when it has no generation state to report. */
export const IDLE_AI_TEXT_STATUS: AITextControlStatus = {
  isGenerating: false,
  error: null,
};

export type ControlConfiguration =
  | SpeakerControlConfiguration
  | TextControlConfiguration
  | ColorControlConfiguration
  | AITextControlConfiguration
  | NumberControlConfiguration
  | FontControlConfiguration
  | ImageControlConfiguration
  | LogoControlConfiguration
  | VideoControlConfiguration
  | HeadshotControlConfiguration;

export interface ControlRenderProps<
  T extends ControlConfiguration = ControlConfiguration,
> {
  control: T;
  activeElement: ElementState;
  modifyProperty: (control: ControlConfiguration, value: unknown) => void;
  // Transient hover preview. When provided, a control routes its hover preview
  // through this instead of the global store, so surfaces that own their
  // renderer/editor (e.g. Pull Quotes) preview on the correct canvas.
  previewProperty?: (control: ControlConfiguration, value: unknown) => void;
  compositionMeta?: Record<string, unknown>;
  onSpeakerControlChange?: (
    controlBindings: Record<string, SpeakerBindingKey>,
    inputValues: Speaker,
  ) => void; // Only for SpeakerControl
  // font_size write path: entered px → stored viewport-relative string. The
  // element id selects the vh basis (containing composition vs. root video).
  convertToVh?: (px: string | null, elementId?: string) => string;
  // font_size read path: stored value ("N vh"/"N vw"/"Npx"/number) → px for
  // display. Inverse of convertToVh — without it a stored "N vh" string can't
  // be parsed and the field renders blank (PLAT-11432). A null value means the
  // element sets no explicit font_size (auto-sized text); resolvers may fall
  // back to the renderer's computed size, or return null to leave the field
  // empty.
  convertToPx?: (
    value: string | number | null,
    elementId?: string,
  ) => number | null;
  isLogoControl?: boolean; // Only for ImageControl to indicate logo usage
  pickerTab?: ColorTabs; // Only for ColorControl to set default tab
  min?: number; // Only for NumberControl to set minimum value
  max?: number; // Only for NumberControl to set maximum value
}

type TimeValue = {
  time: number;
  value: string | number;
};

type ColorType = {
  offset: string | number;
  color: string;
};
type CoordinateType = string | number | TimeValue[];

export interface BaseElement {
  id: string;
  track: number;
  name?: string;
  type?: 'composition' | 'text' | 'video' | 'image' | 'audio' | 'shape';
  x?: CoordinateType;
  y?: CoordinateType;
  width?: CoordinateType;
  height?: CoordinateType;
  x_anchor?: string;
  y_anchor?: string;
  x_alignment?: string;
  y_alignment?: string;
  opacity?: string;
  fit?: 'cover' | 'contain' | 'fill';
}

export interface TextElement extends BaseElement {
  type: 'text';
  text: string;
  font_family?: string;
  font_weight?: string;
  font_size?: string;
  fill_color?: string | ColorType[];
  dynamic?: boolean;
  background_color?: string;
  letter_spacing?: string;
}

export interface ShapeElement extends BaseElement {
  type: 'shape';
  fill_color?: string | ColorType[];
  border_radius?: string;
  stroke_color?: string;
}

export interface MediaElement extends BaseElement {
  type: 'image' | 'video' | 'audio';
  source: string;
}

export interface CompositionElement extends BaseElement {
  type: 'composition';
  elements: AnyElement[];
}

export type AnyElement =
  | CompositionElement
  | MediaElement
  | ShapeElement
  | TextElement
  | BaseElement;
